import { z } from "zod";
//#region src/manifest-census.ts
/**
* The manifest census (2026-09-04-app-pools, I-7): how many published or running manifests still
* declare a deprecated key. The contract release (overview O-2) is gated on every census reading
* zero. site-admin-api serves the route (WS-3, R-015). `zion-ops app census` calls it (WS-8) and exits
* 0 (zero rows, complete), 1 (rows), or 3 (incomplete or unreadable). 3 outranks 1.
*/
/** The operator permission the census route requires (I-31, R-015, R-030). One declaration, so WS-3's
*  guard and WS-8's docs import it instead of restating the literal. The pool read's scope is
*  `APP_POOL_ADMIN_SCOPE` in `app-pool-admin.ts`. */
const MANIFEST_CENSUS_PERMISSION = "cp:platform:admin";
const MANIFEST_CENSUS_PATH = "/v1/admin/manifest-census";
/** `pinned`: versions pinned on any ring (`app_channel_pins`). `running`: versions a data plane
*  reports running (the adoption matrix). */
const MANIFEST_CENSUS_SCOPES = ["pinned", "running"];
const MANIFEST_CENSUS_DOCUMENTS = ["app", "fixture"];
/** Upper bound on the query's `key`, applied before `MANIFEST_CENSUS_POINTER_PATTERN` so the
*  pattern never runs against an unbounded string. The longest pointer the inventory ever held was
*  the container `sizeClass` row, two wildcard hops deep, at 34 characters; the 40.0.0 major removed
*  it, and the 13 rows that stay are shorter. So 256 is generous by an order of magnitude and caps
*  the match input, the derivation `MOUNT_PATH_MAX_LENGTH` carries for the same package. The route
*  accepts any pointer of this grammar, an inventory row or not. */
const MANIFEST_CENSUS_POINTER_MAX_LENGTH = 256;
/** A JSON Pointer whose segments are `[A-Za-z0-9_-]+` or `*` (an array wildcard). */
const MANIFEST_CENSUS_POINTER_PATTERN = /^(\/([A-Za-z0-9_-]+|\*))+$/;
/**
* The optional value a census leaf must equal (2026-10-14-compute-unit-contraction, D-8), for a
* surface that is a value of a live key, such as the services' `topology.mode` with the value
* `per-instance`. The longest value the inventory held was `per-instance`, 12 characters; the 40.0.0
* major removed both value rows, and the route still answers a value query. So 64 is generous and
* caps the input. The grammar admits the enum spellings of the schema and nothing that a jsonpath or SQL
* reader could parse as syntax. site-admin-api passes the value to Postgres only as a jsonpath
* variable (`jsonb_path_exists(doc, path, jsonb_build_object('v', value))`), never in the path text.
*/
const MANIFEST_CENSUS_VALUE_MAX_LENGTH = 64;
const MANIFEST_CENSUS_VALUE_PATTERN = /^[A-Za-z0-9._-]+$/;
/**
* Upper bound on `rows` in one census answer (R-057). Applied as a `LIMIT` on each candidate query
* and matched by `truncated` on the response, so a capped answer never reads as a complete one.
*
* The pointer bound above caps the QUESTION; this caps the ANSWER, and the two are different
* bounds. A pointer every manifest resolves (`/version`, `/appId`) returns one row per published
* composition, and `jsonb_path_exists` over a jsonb column has no index to use, so the result set
* is a sequential scan whose size grows with the fleet. The route is `MANIFEST_CENSUS_PERMISSION`
* only, so this is a self-inflicted cost rather than an exposure; it is still the unbounded-body
* shape this repo's review bots flag.
*
* 1000, derived from the row and not from a round number. A row carries `appId`, `version`,
* `fixtureId` and two small string arrays, which measures in the low hundreds of bytes, so 1000
* rows is a response in the hundreds of kilobytes. That is two orders of magnitude above the
* population O-2 has to drive to zero (21 deprecated surfaces across one fleet's published
* compositions) and an order of magnitude below any response an operator would want streamed. An
* operator who hits the cap has an answer to act on already, and `truncated` tells them there is
* more.
*/
const MANIFEST_CENSUS_ROWS_MAX = 1e3;
const manifestCensusQuerySchema = z.object({
	key: z.string().min(2).max(256).regex(MANIFEST_CENSUS_POINTER_PATTERN),
	document: z.enum(MANIFEST_CENSUS_DOCUMENTS),
	scope: z.enum(MANIFEST_CENSUS_SCOPES),
	value: z.string().min(1).max(64).regex(MANIFEST_CENSUS_VALUE_PATTERN).optional()
}).strict();
/** One manifest that still declares the key: the app, its version, the fixture (null for an app
*  manifest), the rings it is pinned on, and the data planes running it. */
const manifestCensusRowSchema = z.object({
	appId: z.string().min(1),
	version: z.string().min(1),
	fixtureId: z.string().min(1).nullable(),
	rings: z.array(z.string().min(1)),
	dataPlanes: z.array(z.string().min(1))
}).strict();
/** `complete: false` means the CP could not read one of its two sources; the rows collected so far
*  are still returned. The CLI maps it to exit 3. */
const manifestCensusResponseSchema = z.object({
	key: z.string(),
	document: z.enum(MANIFEST_CENSUS_DOCUMENTS),
	scope: z.enum(MANIFEST_CENSUS_SCOPES),
	/** The query's `value`, echoed, so an answer for `mode = per-instance` never reads as an
	*  answer for every `mode`. Absent when the query named no value. */
	value: z.string().optional(),
	measuredAt: z.string().datetime(),
	complete: z.boolean(),
	/**
	* Whether the candidate read hit {@link MANIFEST_CENSUS_ROWS_MAX} (R-057).
	*
	* A different fact from `complete`, and both are needed. `complete: false` is "a source could
	* not be read at all"; `truncated: true` is "every source read cleanly and there are more
	* matches than the cap returns". Folding them would tell an operator whose census is merely
	* capped that the fleet is unreadable, and would tell an operator whose adoption read failed
	* that the answer is merely long.
	*
	* ⛔ It bounds the CANDIDATES and not `rows`. Both scopes filter candidates down, so a
	* `truncated: true` answer can still carry zero rows: the cap was reached and none of the
	* capped candidates was pinned or running. O-2's gate must read `truncated` before it reads a
	* zero, or a capped scan is how "the census reads zero" passes over manifests nobody counted.
	*/
	truncated: z.boolean(),
	rows: z.array(manifestCensusRowSchema).max(MANIFEST_CENSUS_ROWS_MAX)
}).strict();
//#endregion
export { MANIFEST_CENSUS_DOCUMENTS, MANIFEST_CENSUS_PATH, MANIFEST_CENSUS_PERMISSION, MANIFEST_CENSUS_POINTER_MAX_LENGTH, MANIFEST_CENSUS_POINTER_PATTERN, MANIFEST_CENSUS_ROWS_MAX, MANIFEST_CENSUS_SCOPES, MANIFEST_CENSUS_VALUE_MAX_LENGTH, MANIFEST_CENSUS_VALUE_PATTERN, manifestCensusQuerySchema, manifestCensusResponseSchema, manifestCensusRowSchema };

//# sourceMappingURL=manifest-census.js.map